<template>
  <div class="px-2 py-2 border-b" :class="{ 'bg-red-lightest': already_invited }">
    <!-- Widths match the header in InviteMembers.vue. Errors show as text under the field
         instead of only a red border. -->
    <div class="flex flex-row items-start">
      <div class="flex-1 min-w-0">
        <input type="email" class="w-full h-10 border rounded px-3 text-sm" v-model="invite.email"
          :placeholder="'e.g. john@the' + vault_name + 'vault.com'" name="invite_email" data-vv-as="Email"
          v-validate="'required|email'" :class="{ 'border-red': errors.has('invite_email') }" />
        <p class="text-xs text-red mt-1" v-if="errors.has('invite_email')">{{ errors.first('invite_email') }}</p>
      </div>
      <div class="w-32 ml-2 flex-none">
        <input type="text" class="w-full h-10 border rounded px-3 text-sm" v-model="invite.first_name" placeholder="e.g. John"
          name="invite_first_name" data-vv-as="First name" v-validate="'required'"
          :class="{ 'border-red': errors.has('invite_first_name') }" />
        <p class="text-xs text-red mt-1" v-if="errors.has('invite_first_name')">{{ errors.first('invite_first_name') }}</p>
      </div>
      <div class="w-32 ml-2 flex-none">
        <input type="text" class="w-full h-10 border rounded px-3 text-sm" v-model="invite.last_name" placeholder="e.g. Doe"
          name="invite_last_name" data-vv-as="Last name" v-validate="'required'"
          :class="{ 'border-red': errors.has('invite_last_name') }" />
        <p class="text-xs text-red mt-1" v-if="errors.has('invite_last_name')">{{ errors.first('invite_last_name') }}</p>
      </div>
      <div class="w-32 ml-2 flex-none">
        <input type="text" class="w-full h-10 border rounded px-3 text-sm" v-model="invite.mobile_number" placeholder="0400 000 000"
          name="invite_mobile_number" data-vv-as="Mobile" v-validate="'required'"
          :class="{ 'border-red': errors.has('invite_mobile_number') }" />
        <p class="text-xs text-red mt-1" v-if="errors.has('invite_mobile_number')">{{ errors.first('invite_mobile_number') }}</p>
      </div>
      <div class="w-48 ml-2 flex-none">
        <div class="permission_role w-full">
          <multiselect v-model="invite.role" :class="{ invalid: errors.has('invite_role') }" track-by="id"
            :label="vault_type == 'strata' ? 'title' : 'role'" :options="roles" :showLabels="false" name="invite_role"
            data-vv-as="Permission profile" placeholder="Select" v-validate="'required'" :allow-empty="false"
            :searchable="false" @input="checkRole">
          </multiselect>
        </div>
        <p class="text-xs text-red mt-1" v-if="errors.has('invite_role')">{{ errors.first('invite_role') }}</p>

        <div class="w-full mt-2" v-if="set_expiry && isUserF">
          <div class="flex">
            <flat-pickr :config="flatPickrConfig" :events="flatPickrEvents"
              class="appearance-none border rounded-l w-full py-2 px-3 text-sm text-black"
              placeholder="Start date" v-model="invite.start_date" name="start_date" data-vv-as="Start date"
              v-validate="set_expiry && invite.start_date == '' ? 'required' : ''"
              :class="{ 'border-red': errors.has('start_date') }" ref="flatPickerStart"></flat-pickr>
            <button type="button" class="text-white py-2 px-3 rounded-r" :class="errors.has('start_date') ? 'bg-red' : 'bg-brand'" data-toggle aria-label="Pick start date">
              <i class="fa fa-calendar-alt"></i>
            </button>
          </div>
          <p class="text-xs text-red mt-1" v-if="errors.has('start_date')">{{ errors.first('start_date') }}</p>
        </div>
        <div class="w-full mt-2" v-if="set_expiry">
          <div class="flex">
            <flat-pickr :config="flatPickrConfig" :events="flatPickrEvents"
              class="appearance-none border rounded-l w-full py-2 px-3 text-sm text-black"
              placeholder="Expiry date" v-model="invite.expiry_days" name="expiry_days" data-vv-as="Expiry date"
              v-validate="set_expiry && invite.expiry_days == '' ? 'required' : ''"
              :class="{ 'border-red': errors.has('expiry_days') }" ref="flatPickerExpiry"></flat-pickr>
            <button type="button" class="text-white py-2 px-3 rounded-r" :class="errors.has('expiry_days') ? 'bg-red' : 'bg-brand'" data-toggle aria-label="Pick expiry date">
              <i class="fa fa-calendar-alt"></i>
            </button>
          </div>
          <p class="text-xs text-red mt-1" v-if="errors.has('expiry_days')">{{ errors.first('expiry_days') }}</p>
        </div>
      </div>
      <div class="w-20 ml-2 flex-none">
        <input type="text" class="w-full h-10 border rounded px-3 text-sm"
          :placeholder="vault_type == 'strata' ? 'e.g. 1' : 'e.g. Manager'" v-model="invite.unit_number" />
      </div>
      <div class="w-10 ml-2 flex-none">
        <button type="button" class="w-full h-10 border border-red bg-white text-red rounded hover:bg-red hover:text-white"
          @click.prevent="removeInvitation(index)" aria-label="Remove this row">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>
    <p class="text-sm text-red mt-2" v-if="already_invited">
      This person already has a pending invitation to this vault.
    </p>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import flatPickr from "vue-flatpickr-component";
import { roleNeedsExpiry } from "../../vault_roles.mjs";

export default {
  props: ["invite", "roles", "index", "invited-members"],

  components: {
    flatPickr,
  },

  data() {
    return {
      set_expiry: false,
      already_invited: false,

      flatPickrConfig: {
        wrap: true,
        allowInput: false,
        dateFormat: "d/m/Y h:i K",
        enableTime: true,
        monthSelectorType: "static",
      },
      flatPickrEvents: [
        "on-change", "on-close", "on-destroy", "on-key-down", "on-month-change",
        "on-open", "on-parse-config", "on-ready", "on-value-update",
      ],
    };
  },

  methods: {
    validateAndGet(vaults) {
      this.already_invited = false;

      // Returns a promise that settles once every duplicate check is done, so
      // the parent can send the invitations right after (GLOBAL-VAULTS-1ZY).
      return this.$validator.validateAll().then((result) => {
        if (!result) {
          return;
        }

        if (this.checkIfInvitedAndConsumed()) {
          this.already_invited = true;
          return;
        }

        let that = this;
        return Promise.all(_.map(vaults, function (vault) {
          let role = vault.roles.find((role) => {
            return role.title == that.invite.role.title;
          });

          return axios
            .get(`api/vaults/${vault.id}/users`, {
              params: {
                name: that.invite.first_name + " " + that.invite.last_name,
                email: that.invite.email,
                role_id: role.id,
              },
            })
            .then(({ data }) => {
              if (data.total == 0) {
                that.$set(that.invite, "is_valid", true);
                that.$emit("valid-data", {
                  data: that.invite,
                  index: that.index,
                });
              } else {
                that.already_invited = true;
              }
            })
            .catch((error) => {
              if (error.response) {
                console.log(error.response.data);
                console.log(error.response.status);
                console.log(error.response.headers);

                let data = error.response.data;
                if (!data.success) {
                  if(that.$noty) {
                    that.$noty.error(data.message);
                  }
                }
              } else if (error.request) {
                console.log(error.request);
              } else {
                console.log("Error", error.message);
              }
              console.log(error.config);
            });
        }));
      });
    },

    removeInvitation(index) {
      this.$emit("remove-invite", index);
    },

    checkRole(input) {
      this.invite.role = input;
      this.set_expiry = roleNeedsExpiry(input, this.$store.getters.getCurrentVault.id);
    },

    checkIfInvitedAndConsumed() {
      let invite = _.find(this.invitedMembers, (member) => {
        // A pending row starts without a role, and a row parsed from CSV can
        // carry an empty role, so neither side is guaranteed to be an object.
        let member_role_id = member.role ? member.role.id : null;
        let invite_role_id = this.invite.role ? this.invite.role.id : null;

        if (
          member.email == this.invite.email &&
          member.first_name == this.invite.first_name &&
          member.last_name == this.invite.last_name &&
          !_.isNil(member_role_id) &&
          member_role_id == invite_role_id &&
          member.is_consumed == 0
        ) {
          return member;
        }
      });

      if (_.isNil(invite)) {
        return false;
      }

      if (!_.isNil(invite) && moment(invite.expires_at).isBefore(moment())) {
        return false;
      }

      return true;
    },
  },

  computed: {
    vault_name() {
      return window.vault_type;
    },
    ...mapGetters({
      current_vault: "getCurrentVault",
    }),
    vault_type() {
      return window.vault_type;
    },
    isUserF() {
      if (this.invite.role && this.invite.role.name) {
        const roleName = this.invite.role.name.toLowerCase();
        return roleName.includes('user f');
      }
      return false;
    }
  },

  watch: {
    invite: function (value) {
      this.$nextTick(function () {
        this.checkRole(this.invite.role);
      });
    },
  },
};
</script>

<style scoped>
.permission_role>>>.multiselect__tags {
  padding-bottom: 6px !important;
}
</style>